import { useCallback, useEffect, useState } from 'react'
import { useApi } from '../../core/api/client'
import { useEnv } from '../../core/env/EnvContext'
import { useActiveTokens } from '../../core/auth/TokenProvider'
import { roleLabel } from '../../core/auth/roles'
import { COUNTRIES, ENVIRONMENTS, type CountryCode, type Environment } from '../../core/env/types'

interface Credential {
  id: string
  friendly_name: string
  phone_number: string
  country: CountryCode
  environment: Environment
  updated_at: string
  last_role: string | null // role of the most recently minted token from this credential
}

interface MintedToken {
  id: string
  accessToken: string
  friendlyName: string
  environment: Environment
  createdAt: string
  expiresAt: string
}

export function AuthTokenView() {
  const api = useApi()
  const env = useEnv()
  // tokens come from the shared context so minting here updates the header live
  const { tokens, setActiveId, refresh: refreshTokens } = useActiveTokens()

  const [creds, setCreds] = useState<Credential[]>([])
  const [result, setResult] = useState<MintedToken | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // New-credential / quick-mint form. Country + env default to the shared context.
  const [friendlyName, setFriendlyName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [pin, setPin] = useState('')
  const [country, setCountry] = useState<CountryCode>(env.country)
  const [environment, setEnvironment] = useState<Environment>(env.environment)
  const [save, setSave] = useState(true)

  const refreshCreds = useCallback(async () => {
    try {
      setCreds(await api.get<Credential[]>('/credentials'))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [api])

  useEffect(() => { void refreshCreds() }, [refreshCreds])

  async function mint(body: Record<string, unknown>) {
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      const token = await api.post<MintedToken>('/auth/token', body)
      setResult(token)
      await Promise.all([refreshCreds(), refreshTokens()])
      setActiveId(token.id) // make the just-minted token the active one
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  function mintFromForm(e: React.FormEvent) {
    e.preventDefault()
    void mint({ friendlyName: friendlyName || undefined, phoneNumber, pin, country, environment, save })
  }

  async function deleteCredential(id: string) {
    try { await api.del(`/credentials/${id}`); await refreshCreds() }
    catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }

  // Which saved credential (if any) is open in the inline editor.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  async function saveCredential(id: string, body: CredentialEdit) {
    setBusy(true)
    setError(null)
    try {
      const r = await api.put<{ pinChanged: boolean }>(`/credentials/${id}`, body)
      setNotice(`Saved “${body.friendlyName}”${r.pinChanged ? ' with a new PIN' : ''}.`)
      setEditingId(null)
      await Promise.all([refreshCreds(), refreshTokens()])
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function deleteToken(id: string) {
    try { await api.del(`/tokens/${id}`); await refreshTokens() }
    catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }

  // Renew a stored token in place (refresh-token grant → re-mint fallback).
  async function refreshToken(id: string) {
    setBusy(true)
    setError(null)
    try {
      const token = await api.post<MintedToken>(`/tokens/${id}/refresh`, {})
      setResult(token)
      await refreshTokens()
      setActiveId(token.id)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function copy(text: string) {
    try { await navigator.clipboard.writeText(text) } catch { /* clipboard blocked */ }
  }

  async function copyExisting(id: string) {
    try {
      const r = await api.get<{ accessToken: string }>(`/tokens/${id}/reveal`)
      await copy(r.accessToken)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className="module">
      <h2>Token Minter</h2>
      <p className="muted">{authTokenDescription}</p>

      {error && <div className="banner error">{error}</div>}
      {notice && (
        <div className="banner success row spread">
          <span>{notice}</span>
          <button className="ghost" onClick={() => setNotice(null)}>Dismiss</button>
        </div>
      )}

      {result && (
        <div className="banner success result-card">
          <div>
            <strong>Token minted</strong> · {result.friendlyName} · {result.environment} · expires{' '}
            {new Date(result.expiresAt).toLocaleTimeString()}
          </div>
          <code className="token">{result.accessToken}</code>
          <div className="row">
            <button onClick={() => copy(result.accessToken)}>Copy token</button>
          </div>
        </div>
      )}

      <section>
        <h3>Saved credentials</h3>
        {creds.length === 0 && <p className="muted">None yet — mint with “save” checked to store one.</p>}
        <ul className="list">
          {creds.map((c) => (
            <li key={c.id} className={editingId === c.id ? 'editing' : undefined}>
              <div className="row spread">
                <span title={`Last updated ${new Date(c.updated_at).toLocaleString()}`}>
                  <strong>{c.friendly_name}</strong> · {c.phone_number} · {c.country} · {c.environment}
                  {c.last_role && <> · <span className="tag">{roleLabel(c.last_role)}</span></>}
                </span>
                <span className="row">
                  <button disabled={busy} onClick={() => mint({ credentialId: c.id })}>Mint</button>
                  <button className="ghost" onClick={() => setEditingId(editingId === c.id ? null : c.id)}>
                    {editingId === c.id ? 'Close' : 'Edit'}
                  </button>
                  <button className="ghost" onClick={() => deleteCredential(c.id)}>Delete</button>
                </span>
              </div>
              {editingId === c.id && (
                <CredentialEditor
                  credential={c}
                  busy={busy}
                  onSave={(body) => saveCredential(c.id, body)}
                  onCancel={() => setEditingId(null)}
                />
              )}
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h3>New credential / quick mint</h3>
        <form className="form" onSubmit={mintFromForm}>
          <label>Friendly name <input value={friendlyName} onChange={(e) => setFriendlyName(e.target.value)} placeholder="optional" /></label>
          <label>Phone number <input value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required placeholder="07…, no country code" /></label>
          <label>PIN <input value={pin} onChange={(e) => setPin(e.target.value)} required type="password" /></label>
          <label>Country
            <select value={country} onChange={(e) => setCountry(e.target.value as CountryCode)}>
              {COUNTRIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label>Environment
            <select value={environment} onChange={(e) => setEnvironment(e.target.value as Environment)}>
              {ENVIRONMENTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="checkbox"><input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} /> Save this credential</label>
          <button type="submit" disabled={busy}>{busy ? 'Minting…' : 'Mint token'}</button>
        </form>
      </section>

      <section>
        <h3>Recent tokens</h3>
        {tokens.length === 0 && <p className="muted">No tokens minted yet.</p>}
        <ul className="list">
          {tokens.map((t) => (
            <li key={t.id} className="row spread">
              <span>
                <strong>{t.friendly_name}</strong> · <span className="tag">{roleLabel(t.role)}</span> · {t.environment} ·{' '}
                {t.expired ? <span className="tag expired">expired</span> : <span className="tag live">live</span>} ·{' '}
                {new Date(t.created_at).toLocaleString()}
              </span>
              <span className="row">
                <button disabled={t.expired} onClick={() => copyExisting(t.id)}>Copy</button>
                <button disabled={busy} onClick={() => refreshToken(t.id)}>Refresh</button>
                <button className="ghost" onClick={() => deleteToken(t.id)}>Delete</button>
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

interface CredentialEdit {
  friendlyName: string
  phoneNumber: string
  pin?: string
  country: CountryCode
  environment: Environment
}

// Inline editor for one saved credential. Pre-filled from the stored row; the
// PIN field starts empty and only overwrites the stored PIN when filled in
// (PINs get rotated, everything else rarely changes). "Reveal" fetches the
// currently stored PIN so you can check it before deciding to replace it.
function CredentialEditor({
  credential, busy, onSave, onCancel,
}: {
  credential: Credential
  busy: boolean
  onSave: (body: CredentialEdit) => void
  onCancel: () => void
}) {
  const api = useApi()
  const [friendlyName, setFriendlyName] = useState(credential.friendly_name)
  const [phoneNumber, setPhoneNumber] = useState(credential.phone_number)
  const [pin, setPin] = useState('')
  const [showPin, setShowPin] = useState(false)
  const [country, setCountry] = useState<CountryCode>(credential.country)
  const [environment, setEnvironment] = useState<Environment>(credential.environment)
  const [storedPin, setStoredPin] = useState<string | null>(null)
  const [revealError, setRevealError] = useState<string | null>(null)

  async function reveal() {
    setRevealError(null)
    try {
      const r = await api.get<{ pin: string }>(`/credentials/${credential.id}/reveal`)
      setStoredPin(r.pin)
    } catch (e) {
      setRevealError(e instanceof Error ? e.message : String(e))
    }
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    onSave({ friendlyName, phoneNumber, pin: pin || undefined, country, environment })
  }

  return (
    <form className="form cred-edit" onSubmit={submit}>
      <label>Friendly name <input value={friendlyName} onChange={(e) => setFriendlyName(e.target.value)} required /></label>
      <label>Phone number <input value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required /></label>
      <label>
        New PIN <span className="muted">leave blank to keep the stored PIN</span>
        <span className="row">
          <input
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            type={showPin ? 'text' : 'password'}
            placeholder="unchanged"
            autoComplete="new-password"
          />
          <button type="button" className="ghost" onClick={() => setShowPin((v) => !v)}>{showPin ? 'Hide' : 'Show'}</button>
        </span>
      </label>
      <div className="row cred-stored-pin">
        <span className="muted">Stored PIN:</span>
        {storedPin === null
          ? <button type="button" className="ghost" onClick={reveal}>Reveal</button>
          : <code className="mono">{storedPin}</code>}
        {revealError && <span className="muted">{revealError}</span>}
      </div>
      <label>Country
        <select value={country} onChange={(e) => setCountry(e.target.value as CountryCode)}>
          {COUNTRIES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
      <label>Environment
        <select value={environment} onChange={(e) => setEnvironment(e.target.value as Environment)}>
          {ENVIRONMENTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
      <div className="row">
        <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
        <button type="button" className="ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}

const authTokenDescription =
  'Runs the 4-step OAuth flow server-side and stores the bearer token against your identity. Tokens expire after ~1 hour — hit Refresh to renew one, or just keep using it: expired tokens auto-refresh server-side when a module needs them.'
